import { useLayoutEffect, useState, type ReactNode } from 'react';

import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator, {
  HOME_INDICATOR_H,
} from '@/designs-dev/device/HomeIndicator';
import { useHideTabBar } from '@/designs-dev/device/TabBarVisibility';
import AppBar from '@/karehero-app/shell/AppBar';
import Body from '@/karehero-app/shell/Body';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

const SCREEN_PX = 3; // 24px gutter — a touch more breathing room than the 16px screens

// Frosted (translucent + blur) background for the sticky footer CTA.
// backdrop-filter has no system prop / token, so the footer needs sx.
const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

export type StackPageProps = {
  /** Title shown in the header bar. Omit to use the plain "Back" bar (the page
   *  then usually renders its own PageHeader as the first content child). */
  title?: string;
  /** Small line centred under the title in the bar. Needs `title`. */
  subtitle?: string;
  /** Back control. Shown as a chevron beside the title, or as a "Back" text
   *  button when there's no title. Omit for a page with no back affordance. */
  onBack?: () => void;
  /** Text on the no-title back button. Defaults to "Back"; use e.g. "Exit"
   *  when the control leaves a flow rather than stepping back within it. */
  backLabel?: string;
  /** Optional trailing action, pinned right of the title. Keep it to a single
   *  icon or short label so the centred title stays balanced. */
  action?: ReactNode;
  /** Frosted sticky footer, e.g. a primary CTA. Adds bottom scroll padding. */
  footer?: ReactNode;
  /** Hairline under the header. Default true. */
  divider?: boolean;
  /** The scrollable page body. */
  children?: ReactNode;
};

/**
 * The scaffold for a focused / pushed member-app page: device status bar, a
 * back header, a divider, the scrollable content, an optional frosted footer
 * CTA, and the home-indicator safe area. Hides the bottom nav while mounted, so
 * it reads as a focused screen with the back control as the way out.
 *
 * Two header shapes, picked automatically:
 *   • title given  → chevron + title in one row (+ optional action)
 *   • no title     → a "Back" text bar (the page supplies its own PageHeader)
 */
const StackPage = ({
  title,
  subtitle,
  onBack,
  action,
  backLabel,
  footer,
  divider = true,
  children,
}: StackPageProps) => {
  useHideTabBar();

  const hasHeader = Boolean(onBack || title || action);

  // The frosted footer floats over the body, so the body needs bottom padding
  // to clear it — but the footer's height varies (one CTA vs a stacked pair),
  // so measure it rather than reserve a fixed guess the tall case overflows.
  const [footerEl, setFooterEl] = useState<HTMLElement | null>(null);
  const [footerH, setFooterH] = useState(0);
  useLayoutEffect(() => {
    if (!footerEl) return;
    const measure = () => setFooterH(footerEl.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(footerEl);
    return () => ro.disconnect();
  }, [footerEl]);
  // Footer height + a breathing gap; falls back to a sane reserve before measure.
  const bodyPb = footer ? `${(footerH || 72) + 24}px` : 3;

  return (
    <ScreenRoot relative>
      <StatusBar />

      {hasHeader && (
        <AppBar
          title={title}
          subtitle={subtitle}
          onBack={onBack}
          action={action}
          backLabel={backLabel}
          divider={divider}
        />
      )}

      <Body pt={2} pb={bodyPb}>
        {children}
      </Body>

      {/* Floats just above the home indicator so the CTA keeps its safe area. */}
      {footer && (
        <Box
          ref={(el: HTMLElement | null) => setFooterEl(el)}
          sx={[
            {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: HOME_INDICATOR_H,
              px: SCREEN_PX,
              py: 1.5,
            },
            frostedFooter,
          ]}
        >
          {footer}
        </Box>
      )}

      <HomeIndicator />
    </ScreenRoot>
  );
};

StackPage.displayName = 'StackPage';
export default StackPage;
